/*
 * Client-side translation runtime.
 *
 * Ported from the platform team's demo. Detects text nodes via
 * MutationObserver, batches them, calls the existing /Translate
 * endpoint, and applies translations in place. Server-side response
 * filtering is intentionally avoided — the previous IResultFilter
 * approach used async-to-sync bridging which starved the ASP.NET
 * thread pool under load.
 *
 * Configured via window.alisTranslation, emitted by the layout:
 *   {
 *     enabled:        bool,
 *     sourceLanguage: string,
 *     targetLanguage: string
 *   }
 */

const SKIP_TAGS = new Set([
    'SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG', 'TEXTAREA',
    'INPUT', 'CODE', 'PRE', 'IFRAME',
]);

const TRANSLATE_URL = '/Translate';

const Runtime = {
    config: {
        sourceLanguage: 'en',
        targetLanguage: null,
        batchSize: 100,
        debounceMs: 200,
        maxConcurrent: 4,
        root: null,
    },

    cache: new Map(),
    pending: new Set(),
    inFlight: new Set(),
    nodesByText: new Map(),
    translatedValues: new WeakMap(),
    originalText: new WeakMap(),
    touchedNodes: new Set(),
    debounceHandle: null,
    observer: null,
    statusEl: null,
    statusRafHandle: null,
    statusHideTimeout: null,

    init(rootEl, sourceLanguage, targetLanguage) {
        this.config.root = rootEl || document.body;
        this.config.sourceLanguage = sourceLanguage || 'en';
        this.config.targetLanguage = targetLanguage;

        this.createStatusPill();
        this.scan(this.config.root);
        this.observe();
        this.scheduleFlush();
        this.scheduleStatusUpdate();
    },

    /** Walk ancestors looking for a skip tag or translate="no". */
    isInsideSkipTag(node, cache) {
        let cur = node.parentNode;
        const path = [];
        while (cur && cur.nodeType === 1) {
            if (cache && cache.has(cur)) {
                const cached = cache.get(cur);
                for (const p of path) cache.set(p, cached);
                return cached;
            }
            path.push(cur);
            const tag = cur.tagName;
            const upper = tag.length > 5 ? tag.toUpperCase() : tag;
            if (SKIP_TAGS.has(upper) || SKIP_TAGS.has(tag.toUpperCase())) {
                if (cache) for (const p of path) cache.set(p, true);
                return true;
            }
            if (cur.getAttribute && cur.getAttribute('translate') === 'no') {
                if (cache) for (const p of path) cache.set(p, true);
                return true;
            }
            cur = cur.parentNode;
        }
        if (cache) for (const p of path) cache.set(p, false);
        return false;
    },

    /** Find every translatable text node under root and queue it. */
    scan(root) {
        if (!root) return;

        if (root.nodeType === 3) {
            if (!this.isInsideSkipTag(root)) this.queueNode(root, true);
            return;
        }
        if (root.nodeType !== 1) return;

        const rootTag = root.tagName.toUpperCase();
        if (SKIP_TAGS.has(rootTag)) return;
        if (root.getAttribute && root.getAttribute('translate') === 'no') return;

        const skipCache = new Map();
        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (n) => {
                    const v = n.nodeValue;
                    if (!v || !v.trim()) return NodeFilter.FILTER_REJECT;
                    const tv = this.translatedValues;
                    if (tv.has(n) && tv.get(n) === v) return NodeFilter.FILTER_REJECT;
                    if (this.isInsideSkipTag(n, skipCache)) return NodeFilter.FILTER_REJECT;
                    return NodeFilter.FILTER_ACCEPT;
                },
            }
        );

        let n;
        while ((n = walker.nextNode())) {
            this.queueNode(n, true);
        }
    },

    queueNode(node, prevalidated) {
        if (!prevalidated) {
            const tv = this.translatedValues;
            if (tv.has(node) && tv.get(node) === node.nodeValue) return;
            if (this.isInsideSkipTag(node)) return;
        }

        const original = node.nodeValue;
        const trimmed = prevalidated ? original.trim() : (original ? original.trim() : '');
        if (!trimmed) return;

        if (!this.originalText.has(node)) {
            this.originalText.set(node, original);
        }
        this.touchedNodes.add(node);

        const cached = this.cache.get(trimmed);
        if (cached !== undefined) {
            this.applyTranslation(node, trimmed, cached);
            return;
        }

        let nodeSet = this.nodesByText.get(trimmed);
        if (!nodeSet) {
            nodeSet = new Set();
            this.nodesByText.set(trimmed, nodeSet);
        }
        nodeSet.add(node);

        if (!this.inFlight.has(trimmed)) {
            this.pending.add(trimmed);
            this.scheduleStatusUpdate();
        }
    },

    applyTranslation(node, source, translated) {
        const cur = node.nodeValue;
        if (!cur || cur.trim() !== source) return;

        if (source === translated) {
            this.translatedValues.set(node, cur);
            return;
        }

        let newValue;
        if (cur === source) {
            newValue = translated;
        } else {
            const leading = cur.match(/^\s*/)[0];
            const trailing = cur.match(/\s*$/)[0];
            newValue = leading + translated + trailing;
        }

        if (newValue === cur) {
            this.translatedValues.set(node, cur);
            return;
        }

        this.translatedValues.set(node, newValue);
        node.nodeValue = newValue;
    },

    /** Apply a batch of self-induced writes without paying the observer
     *  round-trip for them. Drains external records first, then ours. */
    applyBatch(fn) {
        if (!this.observer) {
            fn();
            return;
        }
        const pre = this.observer.takeRecords();
        if (pre.length) this.handleMutations(pre);

        fn();

        this.observer.takeRecords();
    },

    scheduleFlush() {
        if (this.debounceHandle) clearTimeout(this.debounceHandle);
        this.debounceHandle = setTimeout(() => this.flush(), this.config.debounceMs);
    },

    async flush() {
        this.debounceHandle = null;

        if (this.pending.size === 0) {
            return;
        }

        const epochLang = this.config.targetLanguage;

        const batches = [];
        const pendingIter = this.pending.values();
        const totalToSend = Math.min(
            this.pending.size,
            this.config.batchSize * this.config.maxConcurrent
        );
        let drawn = 0;
        while (drawn < totalToSend && batches.length < this.config.maxConcurrent) {
            const batch = [];
            while (batch.length < this.config.batchSize && drawn < totalToSend) {
                const next = pendingIter.next();
                if (next.done) break;
                batch.push(next.value);
                drawn += 1;
            }
            if (batch.length) batches.push(batch);
        }

        for (const batch of batches) {
            for (const text of batch) {
                this.pending.delete(text);
                this.inFlight.add(text);
            }
        }

        await Promise.all(batches.map(b => this.sendBatch(b, epochLang)));

        if (this.pending.size > 0) {
            this.scheduleFlush();
        }
    },

    async sendBatch(batch, epochLang) {
        let result;
        try {
            result = await callTranslateApi({
                sourceLanguage: this.config.sourceLanguage,
                targetLanguage: epochLang,
                texts: batch,
            });
        } catch (err) {
            console.warn('[Translation] API call failed', err);
            if (this.config.targetLanguage === epochLang) {
                for (const text of batch) this.inFlight.delete(text);
            }
            this.scheduleStatusUpdate();
            return;
        }

        if (this.config.targetLanguage !== epochLang) return;

        this.applyBatch(() => {
            for (const t of result.translations) {
                this.cache.set(t.source, t.translated);
                this.inFlight.delete(t.source);

                const nodes = this.nodesByText.get(t.source);
                if (nodes) {
                    for (const node of nodes) {
                        this.applyTranslation(node, t.source, t.translated);
                    }
                    this.nodesByText.delete(t.source);
                }
            }
        });

        this.scheduleStatusUpdate();
    },

    observe() {
        this.observer = new MutationObserver(records => this.handleMutations(records));
        this.observer.observe(this.config.root, {
            childList: true,
            subtree: true,
            characterData: true,
        });
    },

    handleMutations(records) {
        for (const m of records) {
            if (m.type === 'childList') {
                for (const added of m.addedNodes) {
                    if (added.nodeType === 1) {
                        this.scan(added);
                    } else if (added.nodeType === 3) {
                        this.queueNode(added);
                    }
                }
            } else if (m.type === 'characterData') {
                const node = m.target;
                if (this.translatedValues.has(node) &&
                    this.translatedValues.get(node) === node.nodeValue) {
                    continue;
                }
                this.queueNode(node);
            }
        }
        if (this.pending.size > 0) this.scheduleFlush();
    },

    /** External hook: manually re-scan an element after AJAX renders new
     *  content. The MutationObserver will also catch it, but this lets
     *  $qs.app.bind() integrate the runtime into ALIS's existing flow. */
    rescan(element) {
        if (!this.observer) return;
        this.scan(element || this.config.root);
        if (this.pending.size > 0) this.scheduleFlush();
    },

    /** Build the bottom-right status pill. Follows the AI styling pattern
     *  used elsewhere in the app (mt-tooltip_ai, mt-btn_ai, mt-ai-icon).
     *  translate="no" so the observer doesn't queue its own text. */
    createStatusPill() {
        if (!document.getElementById('alis-translation-styles')) {
            const style = document.createElement('style');
            style.id = 'alis-translation-styles';
            style.textContent = `
                .mt-translate-status {
                    position: fixed;
                    bottom: 20px;
                    left: 20px;
                    z-index: 10000;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 14px 6px 10px;
                    border: 1px solid transparent;
                    border-radius: 999px;
                    background:
                        linear-gradient(#fff, #fff) padding-box,
                        linear-gradient(45deg, #6C7EDA 0%, #0CAFEB 40%, #3EC99B 100%) border-box;
                    color: #374151;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1;
                    box-shadow: 0 2px 8px rgba(108, 126, 218, 0.18);
                    opacity: 0;
                    transform: translateY(6px);
                    pointer-events: none;
                    transition: opacity 0.25s ease, transform 0.25s ease;
                }
                .mt-translate-status--visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .mt-translate-status-label {
                    background: linear-gradient(45deg, #6C7EDA 0%, #0CAFEB 40%, #3EC99B 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .mt-translate-status--done .mt-translate-status-label {
                    background: none;
                    -webkit-text-fill-color: #16A34A;
                    color: #16A34A;
                }
                .mt-translate-status--done {
                    border-color: transparent;
                    background:
                        linear-gradient(#fff, #fff) padding-box,
                        linear-gradient(45deg, #34D399 0%, #10B981 100%) border-box;
                    box-shadow: 0 2px 10px rgba(16, 185, 129, 0.25);
                    animation: mt-translate-status-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                @keyframes mt-translate-status-pop {
                    0%   { transform: translateY(0) scale(0.92); }
                    60%  { transform: translateY(0) scale(1.06); }
                    100% { transform: translateY(0) scale(1); }
                }
                .mt-translate-status-checkmark {
                    display: inline-block;
                    width: 18px;
                    height: 18px;
                }
                .mt-translate-status-checkmark circle {
                    fill: #10B981;
                }
                .mt-translate-status-checkmark path {
                    fill: none;
                    stroke: #fff;
                    stroke-width: 3;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-dasharray: 20;
                    stroke-dashoffset: 20;
                    animation: mt-translate-status-check 0.3s ease-out 0.05s forwards;
                }
                @keyframes mt-translate-status-check {
                    to { stroke-dashoffset: 0; }
                }
                .mt-translate-status-sparkle {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    animation: mt-translate-status-sparkle-pulse 1.6s ease-in-out infinite;
                    transform-origin: center;
                }
                @keyframes mt-translate-status-sparkle-pulse {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                    50%      { transform: scale(1.12) rotate(8deg); opacity: 0.85; }
                }
            `;
            document.head.appendChild(style);
        }

        const el = document.createElement('div');
        el.className = 'mt-translate-status';
        el.setAttribute('translate', 'no');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);

        this.statusEl = el;
        this.renderStatusBusy();
    },

    renderStatusBusy() {
        if (!this.statusEl) return;
        // Phosphor sparkle icon — same one used by mt-tooltip-icon_ai. Filled
        // with the AI gradient so it matches the pill's border and label.
        const iconSvg =
            '<svg class="mt-translate-status-sparkle" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">' +
                '<defs>' +
                    '<linearGradient id="mt-translate-status-sparkle-grad" x1="0" y1="0" x2="256" y2="256" gradientUnits="userSpaceOnUse">' +
                        '<stop offset="0.15" stop-color="#6C7EDA"/>' +
                        '<stop offset="0.5" stop-color="#0CAFEB"/>' +
                        '<stop offset="0.9" stop-color="#3EC99B"/>' +
                    '</linearGradient>' +
                '</defs>' +
                '<path fill="url(#mt-translate-status-sparkle-grad)" d="M208,144a15.78,15.78,0,0,1-10.42,14.94L146,178l-19,51.62a15.92,15.92,0,0,1-29.88,0L78,178l-51.62-19a15.92,15.92,0,0,1,0-29.88L78,110l19-51.62a15.92,15.92,0,0,1,29.88,0L146,110l51.62,19A15.78,15.78,0,0,1,208,144ZM152,48h16V64a8,8,0,0,0,16,0V48h16a8,8,0,0,0,0-16H184V16a8,8,0,0,0-16,0V32H152a8,8,0,0,0,0,16Zm88,32h-8V72a8,8,0,0,0-16,0v8h-8a8,8,0,0,0,0,16h8v8a8,8,0,0,0,16,0V96h8a8,8,0,0,0,0-16Z"/>' +
            '</svg>';
        this.statusEl.classList.remove('mt-translate-status--done');
        this.statusEl.innerHTML = `${iconSvg}<span class="mt-translate-status-label">Translating&hellip;</span>`;
    },

    renderStatusDone() {
        if (!this.statusEl) return;
        this.statusEl.classList.add('mt-translate-status--done');
        this.statusEl.innerHTML =
            '<svg class="mt-translate-status-checkmark" viewBox="0 0 24 24">' +
                '<circle cx="12" cy="12" r="12"/>' +
                '<path d="M7 12.5l3.2 3.2L17 9"/>' +
            '</svg>' +
            '<span class="mt-translate-status-label">Translated</span>';
    },

    scheduleStatusUpdate() {
        if (this.statusRafHandle != null) return;
        this.statusRafHandle = requestAnimationFrame(() => {
            this.statusRafHandle = null;
            this.refreshStatus();
        });
    },

    refreshStatus() {
        if (!this.statusEl) return;

        const busy = this.pending.size > 0 || this.inFlight.size > 0;

        if (busy) {
            if (this.statusHideTimeout) {
                clearTimeout(this.statusHideTimeout);
                this.statusHideTimeout = null;
            }
            this.renderStatusBusy();
            this.statusEl.classList.add('mt-translate-status--visible');
            return;
        }

        // Brief stickiness so the pill doesn't flicker when batches finish
        // in quick succession. Once truly idle, swap to the success state
        // for a beat, then fade out.
        if (this.statusHideTimeout) return;
        this.statusHideTimeout = setTimeout(() => {
            this.statusHideTimeout = null;
            if (this.pending.size === 0 && this.inFlight.size === 0) {
                this.renderStatusDone();
                setTimeout(() => {
                    if (this.pending.size === 0 && this.inFlight.size === 0) {
                        this.statusEl.classList.remove('mt-translate-status--visible');
                    }
                }, 600);
            }
        }, 300);
    },
};

function callTranslateApi(payload) {
    return $.ajax({
        url: TRANSLATE_URL,
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
            texts: payload.texts,
            sourceLanguage: payload.sourceLanguage,
            targetLanguage: payload.targetLanguage,
        }),
    });
}

function initFromWindowConfig() {
    const cfg = window.alisTranslation;
    if (!cfg || !cfg.enabled || !cfg.targetLanguage) {
        return;
    }

    // document.body so nav, header, footer, modals, and overlays are
    // all in scope. Element types that should never be translated
    // (script, style, input, etc.) are filtered by SKIP_TAGS.
    Runtime.init(document.body, cfg.sourceLanguage || 'en', cfg.targetLanguage);
}

if (typeof window !== 'undefined') {
    window.alisTranslationRuntime = Runtime;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFromWindowConfig);
    } else {
        initFromWindowConfig();
    }
}

export default Runtime;
